import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Package, AlertTriangle, ShoppingCart, TrendingDown, TrendingUp, ArrowRight, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';
import { fmtMoeda } from './insumos.utils';

const OBRA_COLORS = {
  'Benjamin Constant': '#1B3F5E',
  'Lumière': '#4A8FBF',
  'Ingarden': '#6f42c1',
  'Escritório': '#28A745',
  'Manutenção': '#F5A623',
  'Externo': '#fd7e14',
};

function KpiCard({ title, value, icon: Icon, color, onClick }) {
  const cls = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    gray: 'bg-gray-50 text-gray-600 border-gray-100',
  };
  return (
    <div className={`rounded-xl border p-5 ${cls[color]} ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium opacity-80">{title}</span>
        <Icon size={20} className="opacity-60" />
      </div>
      <p className="text-3xl font-bold">{value ?? '—'}</p>
    </div>
  );
}

export default function InsumosDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['insumos-dashboard'],
    queryFn: () => api.get('/insumos/dashboard').then(r => r.data.data),
    refetchInterval: 60000,
  });

  if (isLoading) return <div className="p-6 text-gray-400">Carregando...</div>;

  const { totais = {}, por_obra = [], ultimas_entradas = [], ultimas_saidas = [] } = data || {};

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package size={24} className="text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Insumos — Dashboard</h1>
            <p className="text-sm text-gray-500">Controle de materiais de construção civil</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/insumos/entrada')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            <TrendingUp size={16} /> Entrada (NF)
          </button>
          <button onClick={() => navigate('/insumos/saida')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <TrendingDown size={16} /> Saída
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard title="Materiais cadastrados" value={totais.total_materiais} icon={Package} color="blue" onClick={() => navigate('/insumos/materiais')} />
        <KpiCard title="Críticos" value={totais.criticos} icon={AlertTriangle} color="yellow" onClick={() => navigate('/insumos/materiais')} />
        <KpiCard title="Alertas estoque" value={totais.alertas} icon={AlertTriangle} color={totais.alertas > 0 ? 'red' : 'gray'} onClick={() => navigate('/insumos/alertas')} />
        <KpiCard title="Pedidos pendentes" value={totais.pedidos_pendentes} icon={ShoppingCart} color={totais.pedidos_pendentes > 0 ? 'yellow' : 'gray'} onClick={() => navigate('/insumos/pedidos')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico por obra */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-700 mb-4">Materiais por Obra</h2>
          {por_obra.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={por_obra} layout="vertical" margin={{ left: 80 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="obra" tick={{ fontSize: 11 }} width={80} />
                <Tooltip formatter={(v) => [v, 'Tipos de material']} />
                <Bar dataKey="materiais" radius={[0, 4, 4, 0]}>
                  {por_obra.map((o, i) => (
                    <Cell key={i} fill={OBRA_COLORS[o.obra] || '#4A8FBF'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-gray-400 text-center py-8">Nenhum saldo registrado</p>}
        </div>

        {/* Últimas movimentações */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-700">Últimas Entradas</h2>
            <button onClick={() => navigate('/insumos/entradas')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Ver todas <ArrowRight size={12} />
            </button>
          </div>
          <div className="space-y-2">
            {ultimas_entradas.length ? ultimas_entradas.map(e => (
              <div key={e.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-800">{e.obra}</p>
                  <p className="text-xs text-gray-500">{e.fornecedor_nome || 'Entrada manual'}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock size={10} /> {format(parseISO(e.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                  </span>
                </div>
              </div>
            )) : <p className="text-sm text-gray-400 text-center py-4">Sem entradas recentes</p>}
          </div>
        </div>
      </div>

      {/* Saídas recentes */}
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-700">Últimas Saídas</h2>
          <button onClick={() => navigate('/insumos/saidas')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            Ver todas <ArrowRight size={12} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b">
                <th className="text-left pb-2">Obra</th>
                <th className="text-left pb-2">Responsável</th>
                <th className="text-left pb-2">Data</th>
              </tr>
            </thead>
            <tbody>
              {ultimas_saidas.length ? ultimas_saidas.map(s => (
                <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 font-medium">{s.obra}</td>
                  <td className="py-2 text-gray-600">{s.responsavel_nome}</td>
                  <td className="py-2 text-gray-400 text-xs">{format(parseISO(s.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}</td>
                </tr>
              )) : (
                <tr><td colSpan={3} className="py-6 text-center text-gray-400">Sem saídas recentes</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
